import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Spinner, Alert, Badge,
  Row,
  Col,
  InputGroup
} from 'react-bootstrap';
import { FaTrashAlt, FaInfoCircle, FaEdit, FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchFilter } from '../useSearchFilter';

// get driver api from apiClient
import { driversAPI } from '../../api/apiClient';

// for pagination
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import '../../assets/css/Driver.css';

function Drivers() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredData = useSearchFilter(data, searchTerm, ['email']);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const location = useLocation();
  
  const highlightedUserId = location.state?.scrollToUser;

  useEffect(() => {
    if (location.state?.fromPage) {
      fetchDrivers(location.state.fromPage);
    }
  }, [location.state]);

  // Fetch drivers with pagination
  const fetchDrivers = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await driversAPI.index({
        page,
        per_page: pagination.per_page,
      });

      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        setPagination(response.data.meta || {
          current_page: page,
          last_page: 1,
          per_page: 10,
          total: response.data.data.length,
        });
      } else {
        setError(response.message || t('failed_to_fetch_drivers'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_drivers'));
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!location.state?.fromPage) {
      fetchDrivers();
    }
  }, []);

  useEffect(() => {
    if (highlightedUserId) {
      const element = document.getElementById(`user-${highlightedUserId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [data]);

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await driversAPI.delete(deleteId);
      if (response.success) {
        setShowDelete(false);
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_delete_driver'));
      }
    } catch (err) {
      setError(t('failed_to_delete_driver'));
      console.error("Error deleting driver:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await driversAPI.toggleStatus(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
      console.error("Error toggling driver status:", err);
    }
  };

  const toggleEmailChecked = async (id) => {
    try {
      const response = await driversAPI.emailChecked(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_email'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_email'));
      console.error("Error toggling email:", err);
    }
  };

  const toggleSmsChecked = async (id) => {
    try {
      const response = await driversAPI.smsChecked(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_sms'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_sms'));
      console.error("Error toggling SMS:", err);
    }
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <>
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
        <h3 className="fw-bold mb-0">👤 {t('drivers_list')}</h3>
        <div className='d-flex gap-3'>
          <Button 
            className="btn btn-success" 
            onClick={() => navigate('/admin/drivers/drivers-approved')}
          >
            {t('drivers_approved')}
          </Button>
          <Button 
            className="btn btn-warning" 
            onClick={() => navigate('/admin/drivers/drivers-pending')}
          >
            {t('drivers_pending')}
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              type="text"
              placeholder={t('search_by_email')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button 
                variant="outline-secondary" 
                onClick={clearSearch}
                title={t("reset_search")}
              >
                ✕
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="table-container shadow-sm rounded">
        <div className='table-responsive'>
          <Table bordered hover className="custom-table mb-0 text-center">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>{t('first_name')}</th>
                <th>{t('company_name')}</th>
                <th>{t('last_name')}</th>
                <th>{t('email')}</th>
                <th>{t('phone')}</th>
                <th>{t('rating')}</th>
                <th>{t('status')}</th>
                <th>{t('mail')}</th>
                <th>{t('sms')}</th>
                <th>{t('approved')}</th>
                <th>{t('action')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((driver, index) => (
                <tr key={driver.id} id={`user-${driver.id}`} className={driver.id === highlightedUserId ? "user-highlight" : ""}>
                  <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                  <td>{driver.firstname || '-'}</td>
                  <td>{driver.company_name || '-'}</td>
                  <td>{driver.lastname || '-'}</td>
                  <td>
                    {searchTerm ? (
                      <span 
                        dangerouslySetInnerHTML={{
                          __html: driver.email?.replace(
                            new RegExp(searchTerm, 'gi'), 
                            match => `<mark class="bg-warning">${match}</mark>`
                          )
                        }} 
                      />
                    ) : (
                      driver.email
                    )}
                  </td>
                  <td>{driver.telphone || '-'}</td>
                  <td>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`fs-4 ${star <= Math.round(driver.average_rating || 0) ? 'text-warning' : 'text-muted'}`}
                      >
                        ★
                      </span>
                    ))}
                  </td>
                  <td>
                    <Badge bg={driver.status === 'Approved' ? 'success' : 'danger'}>
                      {driver.status === 'Approved' ? t('active') : t('inactive')}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`mail-${driver.id}`}
                        checked={driver.email_checked}
                        onChange={() => toggleEmailChecked(driver.id)}
                        className="custom-switch"
                      />
                      <span className="toggle-label">
                        {driver.email_checked ? t('on') : t('off')}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`sms-${driver.id}`}
                        checked={driver.sms_checked}
                        onChange={() => toggleSmsChecked(driver.id)}
                        className="custom-switch"
                      />
                      <span className="toggle-label">
                        {driver.sms_checked ? t('on') : t('off')}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Form.Check
                        type="switch"
                        id={`approved-${driver.id}`}
                        checked={driver.admin_approve === "2" || driver.admin_approve === 2}
                        onChange={() => toggleStatus(driver.id)}
                        className="custom-switch"
                      />
                      {/* <span className="toggle-label">
                        {driver.admin_approve === "2" || driver.admin_approve === 2 ? t('approved') : t('pending')}
                      </span> */}
                    </div>
                  </td>
                  <td>
                    <Button
                      variant="outline-info"
                      className="me-2"
                      onClick={() => navigate(`/admin/drivers/details/${driver.id}`, { 
                        state: { fromPage: pagination.current_page, userId: driver.id  } 
                      })}
                      title={t('view_details')}
                    >
                      <FaInfoCircle />
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={() => {
                        setDeleteId(driver.id);
                        setShowDelete(true);
                      }}
                      title={t('delete')}
                    >
                      <FaTrashAlt />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={fetchDrivers}
          disabled={loading}
        />
      )}

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirm_deletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('confirm_delete_driver')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t('deleting') : t('delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>

      <style>{`
        .postal-codes-container {
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 1rem;
          background-color: #f8f9fa;
        }
        
        mark {
          background-color: #ffc107;
          padding: 0.1rem 0.2rem;
          border-radius: 0.2rem;
        }
        
        .user-highlight {
          background-color: #d4edda !important;
          border-left: 4px solid #28a745;
          transition: all 0.3s ease;
        }
        
        .user-highlight td {
          font-weight: 600;
          color: #028d22ff;
        }
      `}</style>
    </>
  );
}

export default Drivers;